import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { pageHeadScripts } from "@/lib/pageJsonLd";
import { RegionPage } from "@/components/RegionPage";
import hero from "@/assets/original/hero-shade.jpg";

export const Route = createFileRoute("/gulf-coast")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Texas Gulf Coast Awnings, Boat Covers & Marine Canvas | Corpus Christi, Rockport, Port Aransas | Texas Made Shade",
      description:
        "Marine-grade boat covers, T-tops, bimini tops, awnings and sail shades for the Texas Gulf Coast — Corpus Christi, Rockport, Port Aransas.",
      keywords:
        "Texas Gulf Coast boat covers, marine canvas Gulf Coast, Corpus Christi boat covers, Rockport marine canvas, Port Aransas bimini tops, Houston awnings, Galveston boat covers, Coastal Bend awnings",
      image: hero,
      socialTitle: "Gulf Coast Marine Canvas, Boat Covers & Awnings — Texas Made Shade",
      socialDescription:
        "Sunbrella® Marine canvas, T-tops, bimini tops and welded awnings serving the Texas Gulf Coast and Coastal Bend.",
    }),
    scripts: pageHeadScripts({
      path: "/gulf-coast",
      name: "Texas Gulf Coast Awnings, Boat Covers & Marine Canvas",
      description: "Marine-grade boat covers, T-tops, bimini tops, awnings and sail shades for the Texas Gulf Coast — Corpus Christi, Rockport, Port Aransas.",
      faqs: [
        { q: "What fabric do you use for Gulf Coast boat covers?", a: "Sunbrella® Marine — the industry-standard solution-dyed acrylic with a 10-year limited warranty against UV and mildew. Stitched with PTFE thread that won't rot in saltwater." },
        { q: "Do you come to the boat or do I have to trailer it to Seguin?", a: "We pattern dockside or in your driveway anywhere on the Coastal Bend, Houston/Galveston and South Padre. Sewing happens in Seguin; fitting happens at your boat." },
        { q: "Will a coastal awning hold up in tropical-storm winds?", a: "Frames are welded aluminum with reinforced wall anchors. For named-storm conditions we recommend removable canvas panels — we can spec the awning for quick fabric strike." },
      ],
    }),
  }),
  component: () => (
    <RegionPage
      region="Texas Gulf Coast"
      tagline="Salt air, sun and sustained coastal wind eat ordinary canvas. Our Gulf Coast covers, T-tops and awnings are spec'd to last where the weather is hardest."
      intro="From Galveston down through Port O'Connor, Rockport, Port Aransas and Corpus Christi, the Texas Gulf Coast lives outdoors — and pays a price in salt corrosion, UV and storm-front winds. Every cover, T-top and bimini we make for the coast uses Sunbrella® Marine fabric, marine-grade thread and stainless or anodized hardware. Patterns are done dockside or in your driveway and stitched at our Seguin shop, then delivered and fitted in person."
      cities={[
        { to: "/corpus-christi", name: "Corpus Christi", county: "Nueces Co." },
        { to: "/rockport", name: "Rockport", county: "Aransas Co." },
        { to: "/port-aransas", name: "Port Aransas", county: "Mustang Island" },
        { to: "/port-oconnor", name: "Port O'Connor", county: "Calhoun Co." },
        { to: "/aransas-pass", name: "Aransas Pass", county: "San Patricio Co." },
        { to: "/portland", name: "Portland", county: "San Patricio Co." },
        { to: "/kingsville", name: "Kingsville", county: "Kleberg Co." },
        { to: "/houston", name: "Houston", county: "Harris Co." },
        { to: "/galveston", name: "Galveston", county: "Galveston Co." },
        { to: "/league-city", name: "League City", county: "Galveston Co." },
        { to: "/south-padre-island", name: "South Padre Island", county: "Cameron Co." },
        { to: "/victoria", name: "Victoria", county: "Victoria Co." },
      ]}
      highlights={[
        { to: "/boat-covers", title: "Boat Covers & Marine Canvas", desc: "Custom-patterned mooring, cockpit and travel covers in Sunbrella® Marine." },
        { to: "/covers-tops", title: "T-Tops & Bimini Tops", desc: "Bay boat T-tops, bimini tops and console covers fitted dockside on the coast." },
        { to: "/awnings", title: "Coastal Awnings", desc: "Welded aluminum frames and salt-rated hardware for beach houses and condos." },
        { to: "/sail-shades", title: "Sail Shades", desc: "Pool, deck and waterfront tensioned shade engineered for coastal wind loads." },
        { to: "/cooler-gun-gear-covers", title: "Cooler & Gear Covers", desc: "Custom canvas for fishing rigs, coolers, T-tops and on-deck gear." },
        { to: "/pergola-covers", title: "Patio & Deck Covers", desc: "Coastal-finish patio covers for beachfront homes and condos." },
      ]}
      faqs={[
        {
          q: "What fabric do you use for Gulf Coast boat covers?",
          a: "Sunbrella® Marine — the industry-standard solution-dyed acrylic with a 10-year limited warranty against UV and mildew. Stitched with PTFE thread that won't rot in saltwater.",
        },
        {
          q: "Do you come to the boat or do I have to trailer it to Seguin?",
          a: "We pattern dockside or in your driveway anywhere on the Coastal Bend, Houston/Galveston and South Padre. Sewing happens in Seguin; fitting happens at your boat.",
        },
        {
          q: "Will a coastal awning hold up in tropical-storm winds?",
          a: "Frames are welded aluminum with reinforced wall anchors. For named-storm conditions we recommend removable canvas panels — we can spec the awning for quick fabric strike.",
        },
        {
          q: "How quickly can you turn around a Corpus Christi or Rockport project?",
          a: "Marine canvas typically ships back to the coast in 2–4 weeks from final patterning. Awning installs run 3–5 weeks depending on hardware.",
        },
      ]}
    />
  ),
});
